import React, { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import {
  Button,
  Image,
  Modal,
  Popconfirm,
  Space,
  Table,
  TableColumnsType,
  Typography,
} from 'antd';
// Api
import { GET_SOCIAL_BOOST_CAMPAIGN_OFFERS } from 'api/campaignV2/queries';
import { RESOLVE_SOCIAL_BOOST_DISPUTE } from 'api/campaignV2/mutations';
// Types
import {
  CampaignOfferStatus,
  SocialBoostDisputeResolution,
} from 'api/graphql-global-types';
import {
  GetSocialBoostCampaignOffers,
  GetSocialBoostCampaignOffersVariables,
  GetSocialBoostCampaignOffers_getSocialBoostCampaignOffers_entities as SocialBoostOffer,
  GetSocialBoostCampaignOffers_getSocialBoostCampaignOffers_entities_deliverablesV2_items_CbDeliverableLinkItemV2 as LinkDeliverableItem,
  GetSocialBoostCampaignOffers_getSocialBoostCampaignOffers_entities_deliverablesV2_items_CbDeliverableFilesItemV2 as FilesDeliverableItem,
  GetSocialBoostCampaignOffers_getSocialBoostCampaignOffers_entities_deliverablesV2_items_CbDeliverableCopyV2 as CopyDeliverableItem,
  GetSocialBoostCampaignOffers_getSocialBoostCampaignOffers_entities_deliverablesV2_items_CbDeliverableFilesItemV2_files as DeliverableFile,
} from 'api/campaignV2/types/GetSocialBoostCampaignOffers';
import {
  ResolveSocialBoostDispute,
  ResolveSocialBoostDisputeVariables,
} from 'api/campaignV2/types/ResolveSocialBoostDispute';
// Ui
import { errorNotification, successNotification } from 'ui/Notification';
import { DetailCard } from 'ui/DetailList/DetailList';

const { Text, Link, Title } = Typography;

// Pulls the submitted proof (post URL + screenshots + optional caption) and the
// brand's rejection feedback out of an offer's deliverable so the admin can see
// what the talent submitted and why the brand rejected it before resolving.
const getDisputeDetails = (offer: SocialBoostOffer) => {
  const deliverable = offer?.deliverablesV2?.[0];
  const items = deliverable?.items ?? [];

  const link = items.find(
    (item): item is LinkDeliverableItem =>
      item.__typename === 'CbDeliverableLinkItemV2'
  )?.link;
  const files =
    items.find(
      (item): item is FilesDeliverableItem =>
        item.__typename === 'CbDeliverableFilesItemV2'
    )?.files ?? [];
  const caption = items.find(
    (item): item is CopyDeliverableItem =>
      item.__typename === 'CbDeliverableCopyV2'
  )?.copy;

  // One note per revision (review round). The feedback is duplicated across a
  // revision's items on the backend, so take the first non-empty per revision.
  const feedback: string[] = (deliverable?.revisions ?? [])
    .map((revision) => revision.items.find((item) => item.feedback)?.feedback)
    .filter((note): note is string => Boolean(note));

  return { link, files, caption, feedback };
};

type SocialBoostDisputeResolutionProps = {
  campaignId: string;
};

const SocialBoostDisputeResolutionComponent = ({
  campaignId,
}: SocialBoostDisputeResolutionProps): JSX.Element => {
  const [viewOffer, setViewOffer] = useState<SocialBoostOffer | null>(null);

  const { data, loading, refetch } = useQuery<
    GetSocialBoostCampaignOffers,
    GetSocialBoostCampaignOffersVariables
  >(GET_SOCIAL_BOOST_CAMPAIGN_OFFERS, {
    variables: {
      input: {
        campaignId,
        statuses: [CampaignOfferStatus.DELIVERABLES_REJECTED],
      },
    },
  });

  const [resolveSocialBoostDispute, { loading: resolving }] = useMutation<
    ResolveSocialBoostDispute,
    ResolveSocialBoostDisputeVariables
  >(RESOLVE_SOCIAL_BOOST_DISPUTE, {
    onCompleted: () => {
      successNotification('Dispute resolved');
      setViewOffer(null);
      refetch();
    },
    onError: (error) => {
      errorNotification(error.message);
    },
  });

  const handleResolve = (
    offerId: string,
    resolution: SocialBoostDisputeResolution
  ) => {
    resolveSocialBoostDispute({
      variables: {
        input: { offerId, resolution },
      },
    });
  };

  const details = viewOffer ? getDisputeDetails(viewOffer) : null;

  const offers = data?.getSocialBoostCampaignOffers?.entities ?? [];

  const columns: TableColumnsType<SocialBoostOffer> = [
    {
      title: 'Talent',
      key: 'talent',
      render: (_, record) => `${record.firstName} ${record.lastName}`,
    },
    {
      title: 'Submitted At',
      dataIndex: 'submittedAt',
      key: 'submittedAt',
      render: (val: string) => (val ? new Date(val).toLocaleDateString() : '—'),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Button size="small" onClick={() => setViewOffer(record)}>
          View
        </Button>
      ),
    },
  ];

  return (
    <DetailCard title="Social Boost disputes">
      <Table
        rowKey="id"
        loading={loading}
        dataSource={offers}
        columns={columns}
        locale={{ emptyText: 'No disputes' }}
        pagination={false}
      />

      <Modal
        title={
          viewOffer
            ? `Dispute — ${viewOffer.firstName} ${viewOffer.lastName}`
            : 'Dispute'
        }
        visible={!!viewOffer}
        onCancel={() => setViewOffer(null)}
        width={640}
        footer={
          viewOffer ? (
            <Space>
              <Popconfirm
                title="Approve this dispute? The talent gets paid and the share is marked completed."
                okText="Yes, approve"
                cancelText="No"
                onConfirm={() =>
                  handleResolve(
                    viewOffer.id,
                    SocialBoostDisputeResolution.APPROVE
                  )
                }
              >
                <Button type="primary" loading={resolving}>
                  Approve
                </Button>
              </Popconfirm>
              <Popconfirm
                title="Cancel this dispute? The deal is voided and the budget refunded."
                okText="Yes, cancel"
                cancelText="No"
                onConfirm={() =>
                  handleResolve(
                    viewOffer.id,
                    SocialBoostDisputeResolution.CANCEL
                  )
                }
              >
                <Button danger loading={resolving}>
                  Cancel
                </Button>
              </Popconfirm>
              <Popconfirm
                title="Reset this dispute? It reopens for the talent to re-submit."
                okText="Yes, reset"
                cancelText="No"
                onConfirm={() =>
                  handleResolve(
                    viewOffer.id,
                    SocialBoostDisputeResolution.RESET
                  )
                }
              >
                <Button loading={resolving}>Reset</Button>
              </Popconfirm>
            </Space>
          ) : null
        }
      >
        {details && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            <div>
              <Title level={5}>Submitted post</Title>
              {details.link ? (
                <Link href={details.link} target="_blank" rel="noreferrer">
                  {details.link}
                </Link>
              ) : (
                <Text type="secondary">No post URL submitted</Text>
              )}
            </div>

            {details.caption ? (
              <div>
                <Title level={5}>Caption</Title>
                <Text>{details.caption}</Text>
              </div>
            ) : null}

            <div>
              <Title level={5}>Screenshot(s)</Title>
              {details.files.length ? (
                <Space wrap>
                  {details.files.map((file: DeliverableFile) => (
                    <Image
                      key={file.key}
                      src={file.url ?? undefined}
                      alt={file.name}
                      width={160}
                    />
                  ))}
                </Space>
              ) : (
                <Text type="secondary">No screenshot submitted</Text>
              )}
            </div>

            <div>
              <Title level={5}>Brand feedback</Title>
              {details.feedback.length ? (
                <Space direction="vertical">
                  {details.feedback.map((note: string, idx: number) => (
                    <Text key={idx}>“{note}”</Text>
                  ))}
                </Space>
              ) : (
                <Text type="secondary">No feedback provided</Text>
              )}
            </div>
          </Space>
        )}
      </Modal>
    </DetailCard>
  );
};

export default SocialBoostDisputeResolutionComponent;
